import { EmployeeDimensionsDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

interface Props {
  employeeId: number
  legalEntityId: number
}

export interface DimensionValue {
  id: string
  name: string
}

interface BaseDimensionAssignment {
  id: number
  name: string
  options: DimensionValue[]
}

export type SingleValueDimensionAssignment = BaseDimensionAssignment & {
  type: 'single-value'
  assignmentValue?: DimensionValue
  membershipId?: number
}

export type MutipleValueDimensionAssignment = BaseDimensionAssignment & {
  type: 'multiple-value'
  assignments: (DimensionValue & { percentage: number })[]
}

export type DimensionWithEmployeeAssignment =
  | SingleValueDimensionAssignment
  | MutipleValueDimensionAssignment

export const UNASSIGNED = 'unassigned'

const useEmployeeDimensionsAssignments = ({ employeeId, legalEntityId }: Props) => {
  const { company } = useCurrentSession()

  const { data, loading, refetch } = useQuery(EmployeeDimensionsDocument, {
    variables: {
      companyId: company.id,
      employeeId: employeeId,
      legalEntityId: legalEntityId,
    },
  })

  const canSeeEmployeeDimensions =
    data?.employees.employee?._policies?.seeFinanceCustomDimensionsFor.can ?? false
  const canManageEmployeeDimensions =
    data?.employees.employee?._policies?.manageFinanceCustomDimensionsFor.can ?? false
  const thereAreDimensions = (data?.finance.customDimensionsConnection?.nodes?.length || 0) > 0

  const dimensionsWithAssignments: DimensionWithEmployeeAssignment[] = useUnsafeMemo(() => {
    if (loading) {
      return []
    }

    const customDimensionMemberships =
      data?.employees.employee?.financecustomdimensionmembershipsConnection.nodes || []
    const customDimensions = data?.finance.customDimensionsConnection.nodes || []

    const res: DimensionWithEmployeeAssignment[] = []

    customDimensions.forEach((d) => {
      const value = customDimensionMemberships.find(
        (v) => v.customDimensionValue.customDimension.id === d.id
      )

      res.push({
        type: 'single-value',
        id: d.id,
        name: d.name,
        options: d.customdimensionvaluesConnection.nodes.map((v) => ({
          name: v.name,
          id: v.id.toString(),
        })),
        assignmentValue: value
          ? {
              id: value.customDimensionValue.id.toString(),
              name: value.customDimensionValue.name,
            }
          : undefined,
        membershipId: customDimensionMemberships?.find((m) => m.id === value?.id)?.id || undefined,
      })
    })

    return res
  }, [data, loading])

  return {
    isLoading: loading,
    canSeeEmployeeDimensions,
    canManageEmployeeDimensions,
    dimensionsWithAssignments,
    thereAreDimensions,
    refetch,
  }
}
